import { useEffect, useRef, useState } from "react";
import api, { IS_PREVIEW } from "../lib/api";
import { WifiOff, Loader2 } from "lucide-react";

/**
 * Barra superior que aparece apenas quando o backend ou a rede estão indisponíveis.
 * Em produção (always-on) suprime o "acordando" e só mostra "offline" após várias
 * falhas consecutivas.
 */
export default function ConnectionStatus() {
  const [status, setStatus] = useState("ok"); // "ok" | "waking" | "offline"
  const failuresRef = useRef(0);

  useEffect(() => {
    let stop = false;
    let timer = null;

    // Em produção o servidor é always-on: só considerar "waking" após várias
    // falhas seguidas (evita flashar em blips transitórios da rede/ingress).
    const WAKING_THRESHOLD = IS_PREVIEW ? 1 : 3;
    const OFFLINE_THRESHOLD = IS_PREVIEW ? 4 : 6;

    const ping = async (isInitial = false) => {
      if (stop) return;
      if (!navigator.onLine) {
        setStatus("offline");
        return;
      }
      try {
        await api.get("/health", {
          timeout: 25000,
          __noRetry: true,
        });
        failuresRef.current = 0;
        setStatus("ok");
      } catch (err) {
        failuresRef.current += 1;
        if (failuresRef.current >= OFFLINE_THRESHOLD) {
          setStatus("offline");
        } else if (failuresRef.current >= WAKING_THRESHOLD) {
          setStatus("waking");
          timer = setTimeout(() => ping(false), 4000);
        } else {
          // Falha "silenciosa" — tenta de novo em background sem mostrar UI
          timer = setTimeout(() => ping(false), 3000);
        }
      }
    };

    ping(true);
    const id = setInterval(() => ping(false), 60_000);
    const onOnline = () => {
      failuresRef.current = 0;
      ping(false);
    };
    const onOffline = () => setStatus("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      stop = true;
      clearInterval(id);
      if (timer) clearTimeout(timer);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (status === "ok") return null;

  const config = {
    waking: {
      bg: "bg-amber-500",
      icon: <Loader2 size={14} className="animate-spin" />,
      text: IS_PREVIEW
        ? "Servidor acordando... aguarde uns segundos 💤"
        : "Reconectando ao servidor...",
    },
    offline: {
      bg: "bg-red-500",
      icon: <WifiOff size={14} />,
      text: navigator.onLine
        ? "Servidor demorando para responder. Já estamos tentando reconectar…"
        : "Sem conexão com a internet.",
    },
  }[status];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 ${config.bg} text-white text-xs font-semibold py-1.5 px-3 text-center flex items-center justify-center gap-2 shadow-md`}
      data-testid="connection-status"
    >
      {config.icon}
      {config.text}
    </div>
  );
}
